// Wellbeing section (step 3b) for VisitReportPage — DRS §B4/B5 fields.

import { useRef, type JSX } from "react";
import { SelectField } from "./SelectField.js";
import { SwitchToggle } from "./SwitchToggle.js";
import type { FormState, Action } from "../lib/visit-report-types.js";
import { SECTION, HEADING } from "../lib/visit-report-types.js";
import type { SectionAutoSaveBus } from "./VisitReportWizard.js";
import { fieldErrorMessage, type SectionFieldErrors } from "../lib/visit-report-section-validators.js";
import { useFocusOnApiField } from "../hooks/use-focus-on-api-field.js";

const SELECT_BLANK = { value: "", label: "— select —" };
const YN     = [{ value: "no", label: "No" }, { value: "yes", label: "Yes" }];
const SEV    = [SELECT_BLANK, { value: "on_exertion_only", label: "On exertion" }, { value: "at_rest_mild", label: "At rest (mild)" }, { value: "at_rest_severe", label: "At rest (severe)" }];
const TIMING = [SELECT_BLANK, { value: "on_exertion_only", label: "On exertion only" }, { value: "at_rest_mild", label: "At rest — mild" }, { value: "at_rest_severe", label: "At rest — severe" }, { value: "constant", label: "Constant" }];
const EATING = [SELECT_BLANK, { value: "eating_normally", label: "Eating normally" }, { value: "reduced_appetite", label: "Reduced appetite" }, { value: "refusing_food", label: "Refusing food" }, { value: "difficulty_swallowing", label: "Difficulty swallowing" }, { value: "tube_fed", label: "Tube fed" }, { value: "not_assessed", label: "Not assessed" }];
const FLUID  = [SELECT_BLANK, { value: "adequate", label: "Adequate" }, { value: "reduced", label: "Reduced" }, { value: "very_poor", label: "Very poor" }];
const SLEEP  = [SELECT_BLANK, { value: "good", label: "Good" }, { value: "fair", label: "Fair" }, { value: "poor", label: "Poor" }];
const BOWEL  = [SELECT_BLANK, { value: "normal", label: "Normal" }, { value: "constipated", label: "Constipated" }, { value: "diarrhoea", label: "Diarrhoea" }, { value: "unknown", label: "Unknown" }];
const URINE  = [SELECT_BLANK, { value: "normal", label: "Normal" }, { value: "reduced", label: "Reduced" }, { value: "incontinent", label: "Incontinent" }, { value: "catheterised", label: "Catheterised" }, { value: "unknown", label: "Unknown" }];
const SKIN   = [SELECT_BLANK, { value: "intact", label: "Intact" }, { value: "minor_concern", label: "Minor concern" }, { value: "wound_present", label: "Wound present" }, { value: "needs_assessment", label: "Needs assessment" }];
const SELF   = [SELECT_BLANK, { value: "independent", label: "Independent" }, { value: "needs_prompting", label: "Needs prompting" }, { value: "needs_assistance", label: "Needs assistance" }, { value: "dependent", label: "Dependent" }];
const COMM   = [SELECT_BLANK, { value: "clear", label: "Clear" }, { value: "reduced", label: "Reduced" }, { value: "limited", label: "Limited" }, { value: "non_verbal", label: "Non-verbal" }];

const SYMPTOM_FLAGS: { field: keyof Pick<FormState,
  "unplanned_weight_loss_reported" | "fever_or_night_sweats" | "persistent_cough" |
  "dizziness_reported" | "nausea_or_vomiting" | "unusual_fatigue_reported">; label: string }[] = [
  { field: "unplanned_weight_loss_reported", label: "Unplanned weight loss" },
  { field: "fever_or_night_sweats",          label: "Fever or night sweats" },
  { field: "persistent_cough",               label: "Persistent cough" },
  { field: "dizziness_reported",             label: "Dizziness" },
  { field: "nausea_or_vomiting",             label: "Nausea or vomiting" },
  { field: "unusual_fatigue_reported",       label: "Unusual fatigue" },
];

interface WellbeingProps {
  f: FormState; dispatch: (a: Action) => void;
  bus?: SectionAutoSaveBus; fieldErrors?: SectionFieldErrors;
  /** Q9 S7 — when set, the named field is focused after this section renders. */
  focusFieldName?: string | null;
}

export function WellbeingSection({ f, dispatch, bus, fieldErrors, focusFieldName }: WellbeingProps): JSX.Element {
  const fn = (field: Action["field"]) => (v: string) => dispatch({ field, value: v } as Action);
  const err = (api: string): string | undefined => fieldErrorMessage(fieldErrors, api);
  const anySymptom = SYMPTOM_FLAGS.some(({ field }) => f[field] === "yes");
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusOnApiField(containerRef, focusFieldName);

  const setDetail = (key: string, text: string): void =>
    dispatch({ field: "symptom_detail", value: { ...f.symptom_detail, [key]: text } });

  return (
    <div ref={containerRef} className={SECTION}>
      <p className={HEADING}>4 — Wellbeing &amp; Function</p>
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Breathlessness?" value={f.breathlessness_present} onChange={fn("breathlessness_present")} options={YN} error={err("breathlessness_present")} />
        {f.breathlessness_present === "yes" && (
          <SelectField label="Severity" value={f.breathlessness_severity} onChange={fn("breathlessness_severity")} options={SEV} error={err("breathlessness_severity")} />
        )}
        {f.breathlessness_present === "yes" && (
          <SelectField label="Breathlessness timing" value={f.breathlessness_timing} onChange={fn("breathlessness_timing")} options={TIMING} error={err("breathlessness_timing")} />
        )}
        <SelectField label="Eating status" value={f.eating_status} onChange={fn("eating_status")} options={EATING} error={err("eating_status")} />
        <SelectField label="Fluid intake" value={f.fluid_intake} onChange={fn("fluid_intake")} options={FLUID} error={err("fluid_intake_adequate")} />
        <SelectField label="Sleep quality" value={f.sleep_quality} onChange={fn("sleep_quality")} options={SLEEP} error={err("sleep_quality_reported")} />
        <SelectField label="Bowel function" value={f.bowel_function} onChange={fn("bowel_function")} options={BOWEL} error={err("bowel_function")} />
        <SelectField label="Urinary function" value={f.urinary_function} onChange={fn("urinary_function")} options={URINE} error={err("urinary_function")} />
        <SelectField label="Skin condition" value={f.skin_condition} onChange={fn("skin_condition")} options={SKIN} error={err("skin_condition")} />
        <SelectField label="Self-care ability" value={f.self_care_ability} onChange={fn("self_care_ability")} options={SELF} error={err("self_care_ability")} />
        <SelectField label="Communication" value={f.communication_ability} onChange={fn("communication_ability")} options={COMM} error={err("communication_ability")} />
      </div>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mt-4 mb-2">Symptom Flags</p>
      <div className="grid grid-cols-2 gap-2 bg-slate-50 rounded-lg p-3">
        {SYMPTOM_FLAGS.map(({ field, label }) => (
          <SwitchToggle key={field} label={label}
            checked={f[field] === "yes"}
            onChange={(v) => dispatch({ field, value: v ? "yes" : "no" } as Action)}
          />
        ))}
      </div>

      {anySymptom && (
        <div className="mt-3 space-y-2">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Symptom detail</p>
          {SYMPTOM_FLAGS.filter(({ field }) => f[field] === "yes").map(({ field, label }) => (
            <div key={field}>
              <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
              <textarea
                value={f.symptom_detail[field] ?? ""}
                onChange={(e) => setDetail(field, e.target.value.slice(0, 300))}
                onBlur={bus?.onFieldBlur}
                rows={2}
                placeholder="Optional detail…"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
